/**
 * The run viewer's derivations, in one place and away from the components that render them.
 *
 * Two of these are load-bearing rather than convenient. `takenEdgeKey`/`artifactVersionKey`
 * exist because the elapsed clock re-renders this scene once a second: rebuilding the canvas's
 * `takenEdges` or `artifactVersions` inline hands React Flow a fresh array identity every tick,
 * which tore down and re-initialised the viewport ~1×/s in kms — the canvas visibly vanished
 * and reappeared. Memoise on a joined string of the values, never on the array.
 */
import type { FlowDefinition, NodeDefinition } from "@docit/flow-editor";
import type { RunTree } from "~/api/opsTypes";
import type { Run, RunStep } from "~/api/types";

/** The four statuses that mean "this run is still moving" — polling and the clock key off them. */
const LIVE_STATUSES = new Set(["requested", "running", "observing", "paused"]);

export function isLive(status: string): boolean {
  return LIVE_STATUSES.has(status);
}

const TERMINAL_STATUSES = new Set(["completed", "completed-degraded", "failed", "cancelled"]);

export function isTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

/** Canvas node status vocabulary (`NodeRunStatus` in `@docit/flow-editor`). */
export type NodeStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "awaiting-approval"
  // Weave (Docit Work 15 §6.1): a stopped node says WHY it is stopped.
  | "waiting"
  | "queued"
  | "held"
  | "skipped";

/**
 * Persisted steps folded into per-node statuses. A skipped or cancelled step reads as `skipped`,
 * not `pending` and not `failed`: it did not do its work and nothing went wrong — before Weave
 * gave the canvas a `skipped` state this had to borrow `failed`, which accused a node that was
 * simply never reached.
 */
export function statusesFor(steps: RunStep[]): Record<string, NodeStatus> {
  const out: Record<string, NodeStatus> = {};
  for (const step of steps) {
    out[step.nodeId] =
      step.status === "completed"
        ? "completed"
        : step.status === "running"
          ? "running"
          : step.status === "failed" || step.status === "cancelled" || step.status === "skipped"
            ? step.status === "failed"
              ? "failed"
              : "skipped"
            : "pending";
  }
  return out;
}

/** Per-node meta, keyed by nodeId — the canvas inspector's and the Result tab's input. */
export function metasFor(steps: RunStep[]): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const step of steps) {
    if (step.meta) {
      out[step.nodeId] = step.meta;
    }
  }
  return out;
}

/** Consecutive persisted step pairs — the path the executor actually walked. */
export function takenEdgesFor(steps: RunStep[]): Array<{ from: string; to: string }> {
  const ordered = [...steps].sort((a, b) => a.index - b.index);
  const edges: Array<{ from: string; to: string }> = [];
  for (let i = 1; i < ordered.length; i += 1) {
    const from = ordered[i - 1]?.nodeId;
    const to = ordered[i]?.nodeId;
    if (from && to) {
      edges.push({ from, to });
    }
  }
  return edges;
}

/** The memo key: a joined string of the values, so identity only changes when content does. */
export function takenEdgeKey(edges: Array<{ from: string; to: string }>): string {
  return edges.map((edge) => `${edge.from}->${edge.to}`).join("|");
}

export function artifactVersionKey(artifacts: Array<{ path: string; version: number }>): string {
  return artifacts.map((artifact) => `${artifact.path}:${artifact.version}`).join("|");
}

/** A number out of a step's merged `meta`, tolerating the wire's several spellings. */
function metaNumber(meta: Record<string, unknown> | null, ...keys: string[]): number | null {
  if (!meta) {
    return null;
  }
  for (const key of keys) {
    const value = meta[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}

export function metaString(meta: Record<string, unknown> | null, key: string): string | null {
  const value = meta?.[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * A step's token total. `meta.totalTokens` when the executor reported one, otherwise
 * `inputTokens + outputTokens` — never zero-filled, because a Copilot-executed node has no
 * transcript at all and reporting its spend as 0 is a measurement claim nobody can make.
 */
export function stepTokens(step: RunStep): number | null {
  const total = metaNumber(step.meta, "totalTokens");
  if (total !== null) {
    return total;
  }
  const input = metaNumber(step.meta, "inputTokens");
  const output = metaNumber(step.meta, "outputTokens");
  if (input === null && output === null) {
    return step.tokens;
  }
  return (input ?? 0) + (output ?? 0);
}

export interface RunTokenTotals {
  total: number;
  input: number;
  output: number;
  /** How many steps contributed a measurement — a total over 2 of 9 nodes is not the run's. */
  measured: number;
}

/** The Result tab's rollup. Mirrors `sumRunTokens(metas)` from `@docit/flow-editor`. */
export function sumStepTokens(steps: RunStep[]): RunTokenTotals {
  let total = 0;
  let input = 0;
  let output = 0;
  let measured = 0;
  for (const step of steps) {
    const stepTotal = stepTokens(step);
    const stepInput = metaNumber(step.meta, "inputTokens");
    const stepOutput = metaNumber(step.meta, "outputTokens");
    if (stepTotal === null && stepInput === null && stepOutput === null) {
      continue;
    }
    measured += 1;
    total += stepTotal ?? (stepInput ?? 0) + (stepOutput ?? 0);
    input += stepInput ?? 0;
    output += stepOutput ?? 0;
  }
  return { total, input, output, measured };
}

/**
 * How long the run has been going. A live run measures from `startedAt` to now (which is why
 * the caller ticks); a finished one uses the server's own `durationMs` rather than recomputing
 * it from timestamps that may have been rounded on the way out.
 */
export function elapsedMs(run: Run, now: number): number | null {
  if (run.durationMs !== null && !isLive(run.status)) {
    return run.durationMs;
  }
  const started = run.startedAt ?? run.createdAt;
  const from = Date.parse(started);
  if (!Number.isFinite(from)) {
    return run.durationMs;
  }
  return Math.max(0, now - from);
}

/** `18:42:07` — the log pane's and the step log's timestamp. Local time, 24h, never a date. */
export function clockTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return "--:--:--";
  }
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`;
}

/** The graph snapshot `runs.get` sends, before it is shaped for the canvas. */
export interface RunGraphNode {
  id: string;
  name: string;
  /**
   * The definition fields the snapshot carried for this node, when it is a full flow definition
   * (the server stores the definition AS RUN for many flow runs) — the cascade pill's words.
   * Absent on the slim `{id,name}` shape.
   */
  def?: Record<string, unknown>;
}

export interface RunGraphEdge {
  from: string;
  to: string;
  /** The transition's condition, when the snapshot is a definition — the edge's label. */
  condition?: string;
}

export interface RunGraphSnapshot {
  nodes: RunGraphNode[];
  edges: RunGraphEdge[];
  startNodeId?: string;
}

/**
 * The definition fields the run canvas reads off a node: the words it runs on (`prompt`,
 * `instructions`, `purpose`, `description`, a person's `human` question, the tile's description),
 * who runs it (`kind`, `executor`, `role`, `ownerRole`) and on what (`model`, `effort`). Everything
 * else — budgets, tool configs, transitions — stays out: the canvas draws transitions from edges.
 */
const DEF_FIELDS = [
  "kind",
  "prompt",
  "instructions",
  "purpose",
  "description",
  "human",
  "tile",
  "model",
  "effort",
  "role",
  "executor",
  "ownerRole",
  "requireApproval",
  "forEach",
  "subflow",
] as const;

function pickDef(record: Record<string, unknown> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!record) {
    return out;
  }
  for (const key of DEF_FIELDS) {
    const value = record[key];
    if (value !== undefined && value !== null) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * `RunDetail.graph` arrives as raw JSON. Read it defensively and tolerantly: the server sends
 * `{nodes:[{id,name}], edges:[{from,to}]}` today, and a flow definition's own `nodeId`/`name`
 * shape tomorrow — both are accepted, anything else answers `null` so the breakpoint row and
 * the canvas simply do not render rather than rendering an empty graph that looks like a
 * flow with no nodes.
 */
export function readGraph(graph: Record<string, unknown> | null): RunGraphSnapshot | null {
  if (!graph) {
    return null;
  }
  const rawNodes = Array.isArray(graph.nodes) ? graph.nodes : [];
  const nodes: RunGraphNode[] = [];
  for (const entry of rawNodes) {
    if (!entry || typeof entry !== "object") {
      continue;
    }
    const record = entry as Record<string, unknown>;
    const id = typeof record.id === "string" ? record.id : record.nodeId;
    if (typeof id !== "string" || id.length === 0) {
      continue;
    }
    const name = typeof record.name === "string" && record.name.length > 0 ? record.name : id;
    const def = pickDef(record);
    nodes.push(Object.keys(def).length > 0 ? { id, name, def } : { id, name });
  }
  if (nodes.length === 0) {
    return null;
  }

  const rawEdges = Array.isArray(graph.edges) ? graph.edges : [];
  const edges: RunGraphEdge[] = [];
  for (const entry of rawEdges) {
    if (Array.isArray(entry) && typeof entry[0] === "string" && typeof entry[1] === "string") {
      edges.push({ from: entry[0], to: entry[1] });
      continue;
    }
    if (entry && typeof entry === "object") {
      const record = entry as Record<string, unknown>;
      if (typeof record.from === "string" && typeof record.to === "string") {
        edges.push({ from: record.from, to: record.to });
      }
    }
  }
  // A full definition carries no `edges` — its edges are each node's `transitions`.
  if (rawEdges.length === 0) {
    for (const entry of rawNodes) {
      const record = (entry ?? {}) as Record<string, unknown>;
      const from = typeof record.id === "string" ? record.id : record.nodeId;
      const transitions = Array.isArray(record.transitions) ? record.transitions : [];
      for (const t of transitions) {
        const tr = (t ?? {}) as Record<string, unknown>;
        if (typeof from === "string" && typeof tr.nextNodeId === "string" && tr.nextNodeId) {
          edges.push({
            from,
            to: tr.nextNodeId,
            condition: typeof tr.condition === "string" && tr.condition ? tr.condition : "always",
          });
        }
      }
    }
  }
  const start = graph.startNodeId;
  return typeof start === "string" && start
    ? { nodes, edges, startNodeId: start }
    : { nodes, edges };
}

/**
 * The flow the run canvas draws, with every node's words on it (owner, 2026-10-02: the running
 * graph's pills "show the actual prompts").
 *
 * Node list and edges come from the run's graph snapshot, or — when the run recorded none, which
 * is every recent server flow run — from `runs.tree`, which projects the definition the run
 * executes. Each node's definition fields are then layered: the stored flow definition (fetched
 * by slug) first, the snapshot's own copy over it (that is the definition AS RUN), and the tree's
 * kind/model/owner where neither said. Null when there is nothing to draw.
 */
export function runFlowDefinition(input: {
  runName: string;
  graph: RunGraphSnapshot | null;
  tree: RunTree | null;
  /** The flow's stored definition (`{nodes:[…]}`), when it could be read. */
  definition?: unknown;
}): FlowDefinition | null {
  const { runName, graph, tree } = input;
  const treeNodes = tree?.nodes ?? [];
  const nodes: RunGraphNode[] =
    graph?.nodes ?? treeNodes.map((node) => ({ id: node.nodeId, name: node.label || node.nodeId }));
  if (nodes.length === 0) {
    return null;
  }
  const edges: RunGraphEdge[] =
    graph?.edges ??
    (tree?.edges ?? []).map((edge) => ({
      from: edge.from,
      to: edge.to,
      condition: edge.condition,
    }));

  const stored = new Map<string, Record<string, unknown>>();
  const def = input.definition as { nodes?: unknown; startNodeId?: unknown } | null | undefined;
  for (const entry of Array.isArray(def?.nodes) ? def.nodes : []) {
    const record = (entry ?? {}) as Record<string, unknown>;
    const id = typeof record.nodeId === "string" ? record.nodeId : record.id;
    if (typeof id === "string") {
      stored.set(id, record);
    }
  }
  const treeById = new Map(treeNodes.map((node) => [node.nodeId, node]));

  const startNodeId =
    graph?.startNodeId ?? (typeof def?.startNodeId === "string" ? def.startNodeId : undefined);
  return {
    id: "run-graph",
    name: runName,
    ...(startNodeId ? { startNodeId } : {}),
    nodes: nodes.map((node): NodeDefinition => {
      const fromTree = treeById.get(node.id);
      const fallback: Record<string, unknown> = {};
      if (fromTree?.kind) fallback.kind = fromTree.kind;
      if (fromTree?.model) fallback.model = fromTree.model;
      if (fromTree?.ownerRole) fallback.ownerRole = fromTree.ownerRole;
      return {
        ...fallback,
        ...pickDef(stored.get(node.id)),
        ...(node.def ?? {}),
        nodeId: node.id,
        name: node.name,
        transitions: edges
          .filter((edge) => edge.from === node.id)
          .map((edge) => ({ condition: edge.condition ?? "always", nextNodeId: edge.to })),
      };
    }),
  };
}

/** True when the snapshot already carries the words, so fetching the stored definition is moot. */
export function graphCarriesWords(graph: RunGraphSnapshot | null): boolean {
  return !!graph?.nodes.some(
    (node) =>
      !!node.def && ("instructions" in node.def || "prompt" in node.def || "human" in node.def),
  );
}
